import domReady from '@wordpress/dom-ready';

class NavBar {
	constructor(element) {
		this.element = element;
		this.isOpen = false;

		this.toggleButton = this.element.querySelector('[data-nav-toggle]');
		this.overlay = this.element.querySelector('[data-nav-overlay]');
		this.desktopLinks = this.element.querySelectorAll(
			'.nav-menu-desktop .nav-menu-list li>a'
		);
		this.desktopTopLevelLinksWithChildren = this.element.querySelectorAll(
			'.nav-menu-desktop .nav-menu-list > li.menu-item-has-children > a'
		);
		this.desktopTopLevelLinksWithoutChildren =
			this.element.querySelectorAll(
				'.nav-menu-desktop .nav-menu-list > li:not(.menu-item-has-children) > a'
			);

		this.mobileTopLevelLinksWithChildren = this.element.querySelectorAll(
			'div.nav-menu-mobile>ul>li.menu-item.menu-item-has-children>a'
		);
		this.mobileLevel2Links = this.element.querySelectorAll(
			'div.nav-menu-mobile>ul>li.menu-item.menu-item-has-children>ul.sub-menu>li.menu-item>a'
		);

		this.init();
	}

	init() {
		if (!this.toggleButton || !this.overlay) {
			return;
		}

		this.setupAttributes();
		this.bindEvents();
		this.updateNavHeight();
		this.handleScroll();
		this.tallestLevel3DesktopMenu();
	}

	tallestLevel3DesktopMenu() {
		const level2Menus = this.element.querySelectorAll(
			'.nav-menu-desktop .nav-menu-list > li.menu-item-has-children > ul.sub-menu'
		);

		level2Menus.forEach((level2Menu) => {
			const level3Menus = level2Menu.querySelectorAll(
				'li.menu-item-has-children > ul.sub-menu'
			);
			let maxHeight = level2Menu.offsetHeight;
			level3Menus.forEach((menu) => {
				const height = menu.offsetHeight;
				if (height > maxHeight) {
					maxHeight = height;
				}
			});
			level2Menu.style.setProperty(
				'--level3-max-menu-height',
				`${maxHeight}px`
			);
		});
	}

	setupAttributes() {
		this.desktopLinks.forEach((link) => {
			link.setAttribute('data-text', `${link.textContent}v`);
		});
	}

	bindEvents() {
		window.addEventListener('resize', () => this.handleResize());
		window.addEventListener('scroll', () => this.handleScroll(), {
			passive: true,
		});

		this.toggleButton.addEventListener('click', (e) => {
			e.preventDefault();
			this.toggleMenu();
		});

		this.toggleButton.addEventListener('keydown', (e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				this.toggleMenu();
			}
		});

		this.overlay.addEventListener('click', (e) => {
			if (e.target === this.overlay) {
				this.toggleMenu();
			}
		});

		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && this.isOpen) {
				this.toggleMenu();
				this.toggleButton.focus();
			}
		});

		document.addEventListener('click', (e) => this.handleOutsideClick(e));

		this.mobileLevel2Links.forEach((link) => {
			link.addEventListener('click', (e) => {
				e.preventDefault();

				this.handleMobileLevel2Click(e, link);
			});
		});

		this.desktopTopLevelLinksWithChildren.forEach((link) => {
			const parent = link.parentElement;
			link.addEventListener('click', (e) =>
				this.handleDesktopTopLevelClick(e, parent)
			);
			link.addEventListener('mouseenter', () => {
				this.closeOtherDesktopMenus(null);
				parent.classList.add('active');
			});
		});

		this.desktopTopLevelLinksWithoutChildren.forEach((link) => {
			link.addEventListener('mouseenter', () => {
				this.closeOtherDesktopMenus(null);
			});
		});

		//Mobile specific logic
		this.mobileTopLevelLinksWithChildren.forEach((link) => {
			const parent = link.parentElement;
			link.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				this.handleMobileTopLevelClick(e, parent);
			});
		});
	}

	updateNavHeight() {
		const navHeight = this.element.offsetHeight;
		document.documentElement.style.setProperty(
			'--nav-height',
			`${navHeight}px`
		);
	}

	handleResize() {
		if (window.innerWidth >= 768 && this.isOpen) {
			this.toggleMenu();
		}
		this.updateNavHeight();
	}

	handleScroll() {
		const currentScrollY = window.scrollY;
		this.element.classList.toggle('scrolled', currentScrollY > 10);
	}

	toggleMenu() {
		this.isOpen = !this.isOpen;

		this.toggleButton.classList.toggle('active', this.isOpen);
		this.toggleButton.setAttribute('aria-expanded', this.isOpen.toString());
		this.toggleButton.setAttribute(
			'aria-label',
			this.isOpen ? 'Cerrar menú' : 'Abrir menú'
		);

		this.overlay.classList.toggle('active', this.isOpen);
		document.body.classList.toggle('nav-menu-open', this.isOpen);

		if (this.isOpen) {
			const firstFocusable = this.overlay.querySelector(
				'a, button, input, [tabindex]:not([tabindex="-1"])'
			);
			if (firstFocusable) {
				setTimeout(() => firstFocusable.focus(), 100);
			}
		}
	}

	handleMobileTopLevelClick(e, parent) {
		e.preventDefault();
		this.closeOtherMobileMenus(parent);
		parent.classList.toggle('active');
	}

	closeOtherMobileMenus(currentParent) {
		this.mobileTopLevelLinksWithChildren.forEach((item) => {
			if (item.parentElement !== currentParent) {
				item.parentElement.classList.remove('active');
			}
		});
	}

	handleMobileLevel2Click(e, link) {
		const parentLi = link.parentElement;
		const subMenu = parentLi.querySelector('ul.sub-menu');
		const level3Link = subMenu ? subMenu.querySelector('li > a') : null;

		if (level3Link) {
			e.preventDefault();
			window.location.href = level3Link.href;
		}
	}

	handleDesktopTopLevelClick(e, parent) {
		e.preventDefault();
		this.closeOtherDesktopMenus(parent);
		parent.classList.toggle('active');
	}

	closeOtherDesktopMenus(currentParent) {
		this.desktopTopLevelLinksWithChildren.forEach((item) => {
			if (item.parentElement !== currentParent) {
				item.parentElement.classList.remove('active');
			}
		});
	}

	handleOutsideClick(e) {
		if (
			!e.target.closest(
				'.nav-menu-desktop .nav-menu-list > li.menu-item-has-children'
			)
		) {
			this.closeOtherDesktopMenus(null);
		}
	}
}

domReady(() => {
	const navBars = document.querySelectorAll('[data-nav-bar]');
	navBars.forEach((navBar) => new NavBar(navBar));
});
